export type ImageCompressionOptions = {
  maxDimension?: number;
  quality?: number;
  outputType?: "image/jpeg" | "image/webp" | "image/png";
  maxSizeBytes?: number;
};

const DEFAULT_MAX_DIMENSION = 1200;
const DEFAULT_QUALITY = 0.82;
const DEFAULT_MAX_SIZE_BYTES = 600 * 1024; // 600 KB target

const getOutputType = (inputType: string, forced?: ImageCompressionOptions["outputType"]) => {
  if (forced) return forced;
  return "image/webp"; // WebP for all formats — best compression ratio
};

const getFileExtension = (type: string) => {
  if (type === "image/webp") return "webp";
  if (type === "image/png") return "png";
  return "jpg";
};

const replaceExtension = (name: string, type: string) => {
  const ext = getFileExtension(type);
  const base = name.replace(/\.(png|jpe?g|webp)$/i, "");
  return `${base}.${ext}`;
};

const loadImage = (file: File): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Failed to load image"));
    };
    img.src = url;
  });
};

export const compressImageFile = async (file: File, options: ImageCompressionOptions = {}) => {
  if (!file.type.startsWith("image/")) return file;
  if (file.type === "image/gif") return file;

  const maxDimension = options.maxDimension ?? DEFAULT_MAX_DIMENSION;
  const initialQuality = options.quality ?? DEFAULT_QUALITY;
  const maxSizeBytes = options.maxSizeBytes ?? DEFAULT_MAX_SIZE_BYTES;
  const outputType = getOutputType(file.type, options.outputType);

  // Already small enough — re-encoding would only degrade quality.
  // Still rematerialize the bytes: iOS/iCloud "optimized" photos report a
  // Content-Length that doesn't match the readable bytes on the wire, which
  // causes truncated multipart bodies (busboy "Unexpected end of form"). Doing
  // `arrayBuffer()` forces the browser to fully materialize the file so the
  // resulting Blob has a real, matching length.
  if (file.size <= maxSizeBytes) {
    try {
      const buf = await file.arrayBuffer();
      return new File([buf], file.name, { type: file.type, lastModified: file.lastModified });
    } catch {
      return file;
    }
  }

  try {
    const img = await loadImage(file);
    const width = img.width || 1;
    const height = img.height || 1;
    const scale = Math.min(1, maxDimension / Math.max(width, height));
    const targetWidth = Math.max(1, Math.round(width * scale));
    const targetHeight = Math.max(1, Math.round(height * scale));

    const canvas = document.createElement("canvas");
    canvas.width = targetWidth;
    canvas.height = targetHeight;

    const ctx = canvas.getContext("2d");
    if (!ctx) return file;

    ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

    // Iteratively reduce quality until under maxSizeBytes, down to a floor of 0.4
    let blob: Blob | null = null;
    let quality = initialQuality;
    while (quality >= 0.4) {
      blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob(resolve, outputType, quality);
      });
      if (!blob || blob.size <= maxSizeBytes) break;
      quality = Math.round((quality - 0.1) * 10) / 10;
    }

    if (!blob) return file;

    // If we couldn't improve on the original size at all, keep the original
    if (blob.size >= file.size && scale === 1) return file;

    const name = replaceExtension(file.name, outputType);
    return new File([blob], name, { type: outputType, lastModified: file.lastModified });
  } catch (error) {
    return file;
  }
};

export const compressImageFiles = async (files: File[], options?: ImageCompressionOptions) => {
  const tasks = files.map((file) => compressImageFile(file, options));
  return Promise.all(tasks);
};
